<script lang="ts">
    import { analytics } from './analytics';

    export let initialJobs: Array<{
        title: string;
        companyName: string;
        locations: string[];
        slug: string;
    }>;
    export let initialHasMore: boolean = true;
    export let initialPageSize: number = 6;
    // Open the soft "get these by email" modal once the visitor has loaded this many jobs.
    // Server-configured (anti-scraping.soft-prompt-at) and passed through data-props.
    export let softPromptAt: number = 30;

    let jobs = initialJobs;
    let loading = false;
    let error: string | null = null;
    // Start from page 1 since page 0 was already loaded on the server
    let currentPage = 1;
    let hasMore = initialHasMore;
    // Set by the server once the anonymous depth cap is reached (Phase 3a).
    let capped = false;
    // True when the server recognizes the jz_sub light-account cookie — no prompts for them.
    let verified = false;
    const pageSize = 6;

    // Current site filters — pre-fill the alert with what the visitor is looking at.
    const params = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams();
    const keyword = params.get('keyword') || '';
    // The title-search facet. It MUST be carried into every load-more request: page 1 is rendered by
    // the server with the filter applied, so omitting it here would quietly return unfiltered jobs
    // from page 2 onward — the failure looks like "the search stopped working when I scrolled".
    const roles = params.get('roles') || '';
    const industry = params.get('industry') || '';
    const location = params.get('location') || '';

    // Alert signup state
    let email = '';
    let website = ''; // honeypot — must stay empty
    let submitting = false;
    let subscribed = false;
    let signupError: string | null = null;
    let softDismissed = false;

    // Modal state: the prompt is a dialog triggered by "load more", not an inline card.
    let modalOpen = false;
    let modalMode: 'soft' | 'wall' = 'soft';
    let emailInput: HTMLInputElement | null = null;

    function maybeOpenModal() {
        if (verified || subscribed || modalOpen) return;
        if (capped) {
            modalMode = 'wall';
            modalOpen = true;
        } else if (!softDismissed && jobs.length >= softPromptAt) {
            modalMode = 'soft';
            modalOpen = true;
        }
        if (modalOpen) {
            analytics.signupPromptShown(modalMode);
            setTimeout(() => emailInput?.focus(), 0);
        }
    }

    function closeModal() {
        modalOpen = false;
        // Dismissing the soft prompt means "stop asking" for this visit; the wall stays
        // re-openable from the inline card since browsing is blocked anyway.
        if (modalMode === 'soft' && !subscribed) softDismissed = true;
    }

    function reopenWall() {
        modalMode = 'wall';
        modalOpen = true;
        setTimeout(() => emailInput?.focus(), 0);
    }

    async function loadMoreJobs() {
        if (loading || !hasMore) return;

        loading = true;
        error = null;

        try {
            let url = `/api/jobs?page=${currentPage}&size=${pageSize}`;
            if (keyword) url += `&keyword=${encodeURIComponent(keyword)}`;
            if (roles) url += `&roles=${encodeURIComponent(roles)}`;
            if (industry) url += `&industry=${encodeURIComponent(industry)}`;
            if (location) url += `&location=${encodeURIComponent(location)}`;

            const res = await fetch(url);
            if (!res.ok) throw new Error('Failed to load jobs');

            const data = await res.json();
            jobs = [...jobs, ...data.jobs];
            hasMore = data.hasMore;
            capped = data.capped === true;
            verified = data.verified === true;
            // Count the click and how deep they've browsed (page just loaded; total jobs shown).
            analytics.loadMore(currentPage, jobs.length);
            currentPage++;
            maybeOpenModal();
        } catch (err) {
            error = err instanceof Error ? err.message : 'Unknown error';
        } finally {
            loading = false;
        }
    }

    async function submitAlert() {
        if (submitting) return;
        submitting = true;
        signupError = null;

        try {
            const res = await fetch('/api/alerts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // `roles` matters as much as the rest: someone who searched by title chips and then
                // subscribes must get alerts for those titles, not for every job in the city.
                body: JSON.stringify({ email, keyword, roles, industry, location, website })
            });

            const data = await res.json().catch(() => ({}));

            // 400s carry a server message when the rejection is meaningful (e.g. disposable
            // domain); bean-validation 400s have no `error` field and get the generic line.
            if (res.status === 400) {
                signupError = data.error || 'Please enter a valid email address.';
                // Server attaches `error` only for deliberate domain rejections (disposable / no MX);
                // a bare 400 is a bean-validation format bounce.
                analytics.signupRejected(data.error ? 'domain_blocked' : 'invalid_format');
                return;
            }

            if (res.ok && data.ok) {
                subscribed = true;
                analytics.signupCompleted(modalMode);
            } else {
                signupError = data.error || 'Could not sign up. Please try again.';
            }
        } catch {
            signupError = 'Network error. Please try again.';
        } finally {
            submitting = false;
        }
    }
</script>

<svelte:window on:keydown={(e) => { if (e.key === 'Escape' && modalOpen) closeModal(); }} />

{#if jobs.length === 0}
    <p class="muted" style="font-style: italic; padding: 20px 0; text-align: center;">nothing matches. try fewer filters.</p>
{:else}
    <ul class="jobs">
        {#each jobs as job, i}
            <li>
                <a href="/jobs/{job.slug}"
                   on:click={() => analytics.feedJobClick(i + 1, jobs.length, job.companyName)}>{job.title}</a>
                <div class="job-meta">
                    <span class="company">{job.companyName}</span>
                    {#each job.locations ?? [] as loc}
                        <span class="tag">{loc}</span>
                    {/each}
                </div>
            </li>
        {/each}
    </ul>
{/if}

{#if error}
    <p class="flash err" style="margin-top: 14px;">{error}</p>
{/if}

<!-- Persistent state under the list: success confirmation, or the wall once capped
     (the load-more button is gone at that point, so this is what remains in its place). -->
{#if subscribed}
    <div class="alert-cta flash ok" style="margin-top: 18px;">
        <p style="margin:0;">Check your email to confirm your alert (it may land in spam or junk).
            Once confirmed, you can continue browsing.</p>
    </div>
{:else if capped}
    <div class="alert-cta wall" style="margin-top: 18px;">
        <h3 style="margin:0 0 4px;">verify your email to keep browsing</h3>
        <p class="muted" style="margin:0 0 12px;">
            You've reached the end of anonymous browsing. Confirm your email to keep loading jobs.
        </p>
        <button type="button" on:click={reopenWall}>verify my email</button>
    </div>
{/if}

{#if hasMore}
    <p class="load-more-wrap">
        <button class="load-more" on:click={loadMoreJobs} disabled={loading}>
            {loading ? 'loading...' : 'load more'}
        </button>
    </p>
{/if}

<!-- Alert signup modal: soft prompt while browsing, hard wall at the depth cap. -->
{#if modalOpen}
    <div class="modal-backdrop" on:click={closeModal} role="presentation">
        <div
            class="modal"
            class:wall={modalMode === 'wall'}
            role="dialog"
            aria-modal="true"
            aria-label={modalMode === 'wall' ? 'Verify your email to keep browsing' : 'Get these jobs by email'}
            on:click|stopPropagation
        >
            <button class="modal-close" type="button" aria-label="Close" on:click={closeModal}>&times;</button>

            {#if subscribed}
                <h3 style="margin:0 0 8px;">check your email</h3>
                <p class="muted" style="margin:0 0 8px;">
                    {#if modalMode === 'wall'}
                        We sent you a link. Click it to activate your alert, sign in on this device,
                        and continue browsing.
                    {:else}
                        We sent you a link. Click it to activate your alert and sign in on this device.
                    {/if}
                </p>
                <p class="muted" style="margin:0 0 14px;">
                    Don't see it after a minute or two? Check your spam or junk folder.
                </p>
                <button type="button" on:click={closeModal}>done</button>
            {:else}
                {#if modalMode === 'wall'}
                    <h3 style="margin:0 0 4px;">verify your email to keep browsing</h3>
                    <p class="muted" style="margin:0 0 8px;">
                        You've reached the end of anonymous browsing. Confirm your email to keep loading jobs —
                        we'll also email you new matches{#if keyword || industry || location}&nbsp;for your current search{/if}.
                    </p>
                    <p class="muted" style="margin:0 0 12px;">
                        Already have an account? <a href="/alerts/manage">Sign in</a>.
                    </p>
                {:else}
                    <h3 style="margin:0 0 4px;">create an account to continue browsing</h3>
                    <p class="muted" style="margin:0 0 8px;">
                        Keep browsing and get new matching jobs by email.
                    </p>
                    <p class="muted" style="margin:0 0 12px;">
                        Already have an account? <a href="/alerts/manage">Sign in</a>.
                    </p>
                {/if}

                <form class="alert-form" on:submit|preventDefault={submitAlert}>
                    <!-- honeypot: hidden from humans, tempting to bots -->
                    <input
                        type="text"
                        name="website"
                        tabindex="-1"
                        autocomplete="off"
                        bind:value={website}
                        style="position:absolute; left:-9999px;"
                        aria-hidden="true" />

                    <input
                        type="email"
                        name="email"
                        required
                        maxlength="255"
                        placeholder="you@example.com"
                        bind:value={email}
                        bind:this={emailInput}
                        disabled={submitting} />
                    <button type="submit" disabled={submitting}>
                        {submitting ? 'sending...' : modalMode === 'wall' ? 'verify my email' : 'create my account'}
                    </button>
                </form>

                <p class="muted legal">
                    By continuing, you agree to our
                    <a href="/terms" target="_blank" rel="noopener">terms</a> and
                    <a href="/privacy" target="_blank" rel="noopener">privacy policy</a>.
                </p>

                {#if signupError}
                    <p class="flash err" style="margin:10px 0 0;">{signupError}</p>
                {/if}

                {#if modalMode === 'soft'}
                    <button class="link-btn" type="button" on:click={closeModal}>
                        no thanks
                    </button>
                {/if}
            {/if}
        </div>
    </div>
{/if}

<style>
    .alert-cta {
        border: 1px solid #e2e2e2;
        border-radius: 8px;
        padding: 16px;
        background: #fafafa;
    }
    .alert-cta.wall {
        border-color: #4F46E5;
        background: #f5f4ff;
    }
    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 16px;
    }
    .modal {
        position: relative;
        background: #fff;
        border-radius: 10px;
        padding: 22px;
        width: 100%;
        max-width: 420px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    }
    .modal.wall {
        border: 2px solid #4F46E5;
    }
    .modal-close {
        position: absolute;
        top: 8px;
        right: 12px;
        background: none;
        border: none;
        font-size: 22px;
        line-height: 1;
        color: #888;
        cursor: pointer;
        padding: 4px;
    }
    .alert-form {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }
    .alert-form input[type="email"] {
        flex: 1 1 220px;
        padding: 8px 10px;
        border: 1px solid #ccc;
        border-radius: 5px;
    }
    .legal {
        margin: 10px 0 0;
        font-size: 12px;
    }
    .link-btn {
        margin-top: 10px;
        background: none;
        border: none;
        color: #666;
        text-decoration: underline;
        cursor: pointer;
        padding: 0;
        font-size: 13px;
    }
</style>
